import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  Fragment,
} from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Loader } from '@hivebrite/design-system/Loader';
import { PaginationPrevNext } from '@hivebrite/design-system/PaginationPrevNext';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';
import { toast } from '@hivebrite/design-system/Toast';

import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import getRailsType from 'hb-react/frontoffice/apps/LiveFeed/utils/getRailsType';
import toggleLike from 'hb-react/frontoffice/apps/LiveFeed/utils/toggleLike';
import {
  checkMatchingType,
  updateLikesItem,
} from 'hb-react/frontoffice/apps/LiveFeed/utils/updateFeedItemsState';
import ReportContent from 'hb-react/frontoffice/components/ReportContent';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { getBlockedUserIds } from 'hb-react/frontoffice/utils/blockUser';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Filters from './Filters';
import { useInfiniteFeedItems } from './hooks/useInfiniteFeedItems';
import InfiniteLoadObserver from './InfiniteLoadObserver';
import Item from './Item';
import type { FeedItem, FeedProps, FeedImperativeHandle } from './types';
import FromNotificationItem from '../FromNotificationItem';
import { END_OF_FEED_TYPE } from '../Landings/EndOfFeed/constants';

// How many times we load more item before showing the pagination
const LAZY_LOAD_MAX_LOADS = 10;
// How many items per load when lazy loading
const LAZY_LOAD_ITEMS_PER_LOAD = 10;

const FeedWithPagination = forwardRef<FeedImperativeHandle, FeedProps>(
  (
    {
      displayFilters,
      fetchItems,
      filterNoResultText,
      handleUserCanNotAccessLiveFeed,
      initialFilter,
      onTriggerTopPost,
      shouldTriggerAmplitudeOnLike,
      version,
      loadsOnScrollPerPage = LAZY_LOAD_MAX_LOADS,
      itemsPerLoadOnScroll = LAZY_LOAD_ITEMS_PER_LOAD,
    },
    ref,
  ) => {
    const { config, amplitudeConfig } = useLiveFeedContext();
    const { headerHeight } = useUIContext();
    const outboxedFontTheme = useOutboxedFontTheme();
    const amplitude = amplitudeLogger(amplitudeConfig);
    const [currentFilter, setCurrentFilter] = useState(initialFilter);
    const [currentNewItem, setCurrentNewItem] = useState<FeedItem | null>(null);
    const [hiddenUserIds, setHiddenUserIds] =
      useState<number[]>(getBlockedUserIds());
    const [reportedContent, setReportedContent] = useState<FeedItem | null>(
      null,
    );
    const [itemToDelete, setItemToDelete] = useState<FeedItem | null>(null);
    const [itemBeingDeletedId, setItemBeingDeletedId] = useState<number | null>(
      null,
    );

    // Current Page
    const [currentPage, setCurrentPage] = useState(1);

    const isFirstRender = useRef(true);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const {
      fetchNextPage,
      hasNextPage: hasNextLazyLoad,
      isFetching,
      isFetchingNextPage,
      isLoading,
      data,
      total,
      deleteItemMutation,
      filterItems,
      updateItem,
    } = useInfiniteFeedItems({
      initialPageParam: Math.max(
        (currentPage - 1) * loadsOnScrollPerPage + 1,
        1,
      ),
      fetchItems,
      currentFilter,
      maxPages: loadsOnScrollPerPage,
      onUserCanNotAccessLiveFeed: handleUserCanNotAccessLiveFeed,
      perPage: itemsPerLoadOnScroll,
      routes: config.routes,
      topicId: config.topic?.id,
    });

    const hasItems =
      data && data.pages.length > 0 && data.pages[0].items.length > 0;

    const isCurrentNewItem = useCallback(
      (item) =>
        currentNewItem &&
        item.id === currentNewItem.id &&
        item.type === currentNewItem.type,
      [currentNewItem],
    );

    const handleUpdateItem = useCallback(
      ({
        newItem,
        triggerTopPost = false,
      }: { newItem?: FeedItem; triggerTopPost?: boolean } = {}) => {
        if (!newItem) {
          return;
        }
        if (isCurrentNewItem(newItem)) {
          setCurrentNewItem((originalItem) => ({
            ...originalItem,
            ...newItem,
          }));
        } else {
          setCurrentNewItem(null);
        }

        updateItem(newItem);

        if (triggerTopPost) {
          onTriggerTopPost?.({ newItem });
        }
      },
      [isCurrentNewItem, onTriggerTopPost, updateItem],
    );

    const handleDeleteItem = useCallback(async () => {
      if (!itemToDelete) {
        return;
      }
      setItemBeingDeletedId(itemToDelete.postId as number);

      try {
        await deleteItemMutation(itemToDelete);

        if (isCurrentNewItem(itemToDelete)) {
          setCurrentNewItem(null);
        }

        setItemToDelete(null);

        toast.success(__('Successfully destroyed'));
      } catch {
        toast.error(__('Sorry, an error occurred'));
      } finally {
        setItemBeingDeletedId(null);
      }
    }, [itemToDelete, deleteItemMutation, isCurrentNewItem]);

    const handleDeleteVirtualContent = useCallback(
      (item) => filterItems(({ type }) => type !== item.type),
      [filterItems],
    );

    const handleChangeFilters = useCallback(
      (item) => {
        setCurrentFilter(item);
        setCurrentPage(1);
      },
      [setCurrentPage],
    );

    const resetFilters = useCallback(() => {
      setCurrentFilter(initialFilter);
      setCurrentPage(1);
    }, [initialFilter, setCurrentPage]);

    const handleSetHiddenUserIds = useCallback(
      (hiddenUserIds) => setHiddenUserIds(hiddenUserIds),
      [setHiddenUserIds],
    );
    const handleReportContent = useCallback(
      (item) => setReportedContent(item),
      [setReportedContent],
    );
    const closeReportContent = useCallback(
      () => setReportedContent(null),
      [setReportedContent],
    );
    const handleDeleteContent = useCallback(
      (item) => setItemToDelete(item),
      [setItemToDelete],
    );
    const closeDeleteContent = useCallback(
      () => setItemToDelete(null),
      [setItemToDelete],
    );

    const triggerAmplitudeOnLike = useCallback(
      ({ likeId, type }) => {
        // We only want to log "Like" event, not "unlike"
        if (!likeId && shouldTriggerAmplitudeOnLike) {
          amplitude.track('LiveFeed|ContentLiked', {
            landingType: type,
          });
        }
      },
      [amplitude, shouldTriggerAmplitudeOnLike],
    );

    const handleTogglePostLike = useCallback(
      async ({ likeId, likeableId, type }) => {
        triggerAmplitudeOnLike({ likeId, type });

        try {
          // Returns like object if like was created, null if like was deleted
          // @ts-expect-error untyped
          const result = await toggleLike({
            likeableId,
            likeId,
            routes: config.routes,
            type,
          });

          let itemToUpdate: FeedItem | undefined;

          // Find the item to update
          data?.pages?.forEach((page) => {
            if (itemToUpdate) {
              return;
            }

            itemToUpdate = page.items.find(
              (item) =>
                item.id?.toString() === likeableId?.toString() &&
                checkMatchingType(type, item.type),
            );
          });

          if (itemToUpdate) {
            updateItem({
              ...(itemToUpdate as object),
              likes: updateLikesItem({
                element: itemToUpdate,
                like: result,
                increaseCount: !!result,
              }),
            });
          }
        } catch {
          toast.error(__('Sorry, an error occurred'));
        }
      },
      [config.routes, data?.pages, triggerAmplitudeOnLike, updateItem],
    );

    const handleTogglePostLikeFromCurrentNewItem = useCallback(
      ({ likeId, likeableId, type }) => {
        triggerAmplitudeOnLike({ likeId, type });

        // @ts-expect-error untyped
        return toggleLike({
          likeableId,
          likeId,
          routes: config.routes,
          type,
          setCurrentNewItem,
        });
      },
      [config.routes, triggerAmplitudeOnLike],
    );

    const scrollToTopOfTheFeed = useCallback(() => {
      if (wrapperRef.current) {
        const { top } = wrapperRef.current.getBoundingClientRect();

        window.scrollTo({
          top: window.scrollY + top - (headerHeight ?? 0) * 2,
          behavior: 'smooth',
        });
      }
    }, [headerHeight]);

    const handleLoadMoreOnScroll = () => {
      fetchNextPage().catch(() => {
        // catch error but do nothing.
      });

      amplitude.track('LiveFeed|LoadOnScroll', {
        currentPage,
        itemsPerLoadOnScroll,
        loadOnScrollCount: data?.pages.length ?? 0,
        loadsOnScrollPerPage,
      });
    };

    useImperativeHandle(ref, () => ({
      focus: () => wrapperRef.current?.focus(),
      setCurrentNewItem,
    }));

    useEffect(() => {
      setCurrentNewItem(null);
    }, [config, currentFilter, version, currentPage]);

    useEffect(() => {
      amplitude.track('LiveFeed|Viewed');
    }, [amplitude]);

    useEffect(() => {
      // Scroll to the top of the feed every time page changes, but not on the
      // first render.
      if (isFirstRender.current) {
        isFirstRender.current = false;
      } else {
        scrollToTopOfTheFeed();
      }
    }, [currentPage, scrollToTopOfTheFeed]);

    return (
      <>
        {reportedContent && (
          <ReportContent
            id={reportedContent.id}
            // @ts-expect-error untyped
            type={getRailsType(reportedContent.type)}
            onCloseModal={closeReportContent}
          />
        )}
        {itemToDelete && (
          <ConfirmationModal
            confirmButtonLabel={__('Delete post')}
            isLoading={!!itemBeingDeletedId}
            title={__('Delete post')}
            text={__(
              'Are you sure to delete your post? This action is irreversible.',
            )}
            onCloseModal={closeDeleteContent}
            onConfirmation={handleDeleteItem}
          />
        )}

        <Wrapper ref={wrapperRef}>
          {currentNewItem && (
            <Item
              // reset component when we have a new item
              key={currentNewItem.id}
              data-testid="new-post"
              // @ts-expect-error untyped
              hiddenUserIds={hiddenUserIds}
              isActive
              item={currentNewItem}
              itemBeingDeletedId={itemBeingDeletedId}
              onDeleteItem={handleDeleteContent}
              onDeleteVirtualItem={handleDeleteVirtualContent}
              onReportContent={handleReportContent}
              onSetHiddenUserIds={handleSetHiddenUserIds}
              onTogglePostLike={handleTogglePostLikeFromCurrentNewItem}
              onUpdateItem={handleUpdateItem}
              parentType={config.type}
            />
          )}
          <Box display="block" mt={currentNewItem && 4}>
            <FromNotificationItem
              hiddenUserIds={hiddenUserIds}
              onDeleteItem={handleDeleteContent}
              onReportContent={handleReportContent}
              onSetHiddenUserIds={handleSetHiddenUserIds}
              onTogglePostLike={handleTogglePostLike}
              onUpdateItem={handleUpdateItem}
            />
          </Box>

          <ScreenReadersContent>
            <h2>{__('All posts')}</h2>
          </ScreenReadersContent>

          {displayFilters && (
            <Box mt={8} mb={4} justifyContent="flex-end">
              <Filters onChange={handleChangeFilters} value={currentFilter} />
            </Box>
          )}

          {/* Display another message if the user has no content with ['all'] filter */}
          {!hasItems &&
            !isFetching &&
            (currentFilter === null ? (
              <Card>
                <Box justifyContent="center" font="h4-semibold">
                  {__('Welcome! Share the first post for your community!')}
                </Box>
              </Card>
            ) : (
              <ThemeProvider
                theme={outboxedFontTheme.themeName}
                fontBrightness={outboxedFontTheme.fontBrightness}
              >
                {/* filterNoResultText can be a component */}
                {filterNoResultText ? (
                  <Box>{filterNoResultText}</Box>
                ) : (
                  <Box justifyContent="center">
                    <SimpleCard
                      buttonProps={{
                        onClick: resetFilters,
                        children: __('Clear all filters'),
                      }}
                      cardProps={{ width: '100%' }}
                      title={__('No results found')}
                      description={__('Try applying another filter search')}
                      faIconProps={{
                        iconName: 'radar',
                      }}
                    />
                  </Box>
                )}
              </ThemeProvider>
            ))}

          {hasItems &&
            data.pages.map((page, i) => (
              <Fragment key={i}>
                {page.items.map((item) => (
                  <Box
                    key={`${item.type}-${item.id}`}
                    display="block"
                    pb={5}
                    px={{ _: item.type === END_OF_FEED_TYPE ? 4 : 0, m: 0 }}
                  >
                    <Item
                      // @ts-expect-error untyped
                      hiddenUserIds={hiddenUserIds}
                      item={item}
                      onDeleteItem={handleDeleteContent}
                      onDeleteVirtualItem={handleDeleteVirtualContent}
                      onReportContent={handleReportContent}
                      onSetHiddenUserIds={handleSetHiddenUserIds}
                      onTogglePostLike={handleTogglePostLike}
                      onUpdateItem={handleUpdateItem}
                      itemBeingDeletedId={itemBeingDeletedId}
                      parentType={config.type}
                    />
                  </Box>
                ))}
              </Fragment>
            ))}
          {hasNextLazyLoad && !(isFetchingNextPage || isLoading) && (
            <InfiniteLoadObserver
              onIntersect={handleLoadMoreOnScroll}
              thresholdAbove={5000}
            />
          )}
          {(isFetchingNextPage || isLoading) && (
            <Box justifyContent="center" mb={6}>
              <Loader size={40} />
            </Box>
          )}

          {/* Display pagination at the end of lazy loaded list only */}
          {hasItems && !hasNextLazyLoad && (
            <Box mt={1} mb={10} flexDirection="column">
              <PaginationPrevNext
                hasNextPage={
                  !!total &&
                  currentPage * itemsPerLoadOnScroll * loadsOnScrollPerPage <
                    total
                }
                hasPreviousPage={currentPage > 1}
                onPageChange={(direction) => {
                  if (direction === 'next') {
                    setCurrentPage(currentPage + 1);
                  } else {
                    setCurrentPage(currentPage - 1);
                  }
                }}
              />
            </Box>
          )}
        </Wrapper>
      </>
    );
  },
);

export default FeedWithPagination;

const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
  mt: 5,
}))`
  outline: 0;
`;
